import { Link } from 'react-router-dom';
import { Linkedin, Facebook, Mail, ArrowUpRight } from 'lucide-react';

const footerLinks = {
  services: [
    { name: 'AI Consulting', path: '/ai-consulting' },
  ],
  company: [
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ],
  partners: [
    { name: 'Subbly Partner', path: 'https://partners.discovify.ai/subbly', external: true },
    { name: 'JobNimbus Partner', path: 'https://partners.discovify.ai/jobnimbus-partner', external: true },
  ],
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0A1F35] border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <span className="text-2xl font-bold text-white">Discovify <span className="text-[#E53935]">AI</span></span>
            </Link>
            <p className="text-white/60 font-body text-base max-w-md mb-6">
              We help growing businesses find workflow bottlenecks and fix them with AI and automation so teams get more done with less stress.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.linkedin.com/in/bradley-b-j-monacelli-23849872" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-white/5 hover:bg-[#0FA4A9]/20 flex items-center justify-center transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin className="h-5 w-5 text-white/60 hover:text-[#0FA4A9]" />
              </a>
              <a 
                href="https://www.facebook.com/DiscovifyAI" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-white/5 hover:bg-[#0FA4A9]/20 flex items-center justify-center transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="h-5 w-5 text-white/60 hover:text-[#0FA4A9]" />
              </a>
              <a 
                href="mailto:info@discovify.ai" 
                className="w-10 h-10 rounded-sm bg-white/5 hover:bg-[#0FA4A9]/20 flex items-center justify-center transition-colors"
                data-testid="social-email"
              >
                <Mail className="h-5 w-5 text-white/60 hover:text-[#0FA4A9]" />
              </a>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-white mb-6">
              Services
            </h4>
            <ul className="space-y-4">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="font-body text-white/60 hover:text-[#0FA4A9] transition-colors flex items-center gap-1 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-white mb-6">
              Company
            </h4>
            <ul className="space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="font-body text-white/60 hover:text-[#0FA4A9] transition-colors flex items-center gap-1 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
              {/* Partner Links */}
              <li className="pt-2 border-t border-white/10 mt-4">
                <span className="font-body text-xs text-white/40 uppercase tracking-wider">Partners</span>
              </li>
              {footerLinks.partners.map((link) => (
                <li key={link.path}>
                  <a 
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-body text-white/60 hover:text-[#0FA4A9] transition-colors flex items-center gap-1 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="font-body text-sm text-white/40">
            {currentYear} Discovify AI. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="font-body text-sm text-white/40 hover:text-[#0FA4A9] transition-colors" data-testid="footer-privacy-policy-link">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="font-body text-sm text-white/40 hover:text-[#0FA4A9] transition-colors" data-testid="footer-terms-of-service-link">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
